import { apiApplicants } from "@/api/recruit/applicant.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { applicantActionsKeys } from "@/queries/recruit/use-applicant-actions.query.ts";
import { candidateActionsKeys } from "@/queries/recruit/use-candidate-actions.query.ts";
import { candidatesKeys } from "@/queries/recruit/use-candidates.query.ts";
import { jobsKeys } from "@/queries/recruit/use-jobs.query.ts";
import { ApiErrorException, ApiPassException } from "@/types/api.types.ts";
import { ApiPagedData } from "@skailer-com/uikit/core";
import {
  ApiApplicantCreatePayload,
  ApiApplicantFilter,
  ApiApplicantRecommendationFilter,
  ApiApplicantSavePayload,
  ApiApplicantShort,
  ApiCandidate,
  ApplicantFilterDefault,
} from "@/types/recruit/applicant.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback } from "react";
import { Control, FieldValues } from "react-hook-form";

export const applicantsKeys = createQueryKeys("applicants", {
  search: (filter: ApiApplicantFilter) => ({
    queryKey: [filter],
  }),
  searchInfinite: (filter: ApiApplicantFilter) => ({
    queryKey: [filter],
  }),
  aiRecommendations: (filter: ApiApplicantRecommendationFilter) => ({
    queryKey: [filter],
  }),
  short: (id: number | undefined) => ({
    queryKey: [id],
  }),
  detail: (id: number | undefined) => ({
    queryKey: [id],
  }),
  mentions: () => ({
    queryKey: ["mentionsCount"],
    queryFn: async () => {
      const result = await apiApplicants.mentions();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useApplicantsSearchInfinite = (filter: ApiApplicantFilter) => {
  const queryClient = useQueryClient();

  return useInfiniteQuery({
    queryKey: applicantsKeys.searchInfinite(filter).queryKey,
    queryFn: async ({ pageParam = 1, signal }) => {
      const result = await apiApplicants.search({ ...filter, page_number: pageParam }, signal);
      if (result.error) throw new ApiErrorException(result.error);
      result.data?.items.forEach((item) => {
        queryClient.setQueryData(applicantsKeys.short(item.id).queryKey, item);
        queryClient.setQueryData(
          candidatesKeys.listByApplicantId(item.id).queryKey,
          item.candidates
        );
      });
      return result.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPageParam) =>
      lastPageParam && lastPageParam.metadata.page_number < lastPageParam.metadata.total_pages
        ? lastPageParam.metadata.page_number + 1
        : undefined,
  });
};

export const useApplicantsSearch = (filter: ApiApplicantFilter, enabled: boolean = true) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: applicantsKeys.search(filter).queryKey,
    queryFn: async ({ signal }) => {
      const result = await apiApplicants.search({ ...filter }, signal);
      if (result.error) throw new ApiErrorException(result.error);
      result.data?.items.forEach((item) => {
        queryClient.setQueryData(applicantsKeys.short(item.id).queryKey, item);
        queryClient.setQueryData(
          candidatesKeys.listByApplicantId(item.id).queryKey,
          item.candidates
        );
      });
      return result.data;
    },
    enabled: enabled,
  });
};

export const useApplicantShortCache = (id: number | undefined) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: applicantsKeys.short(id).queryKey,
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("No id");

      const result = await apiApplicants.search({
        page_number: 1,
        applicant_ids: [id],
        sorting: ApplicantFilterDefault.sorting,
      });

      if (result.error) throw new ApiErrorException(result.error);
      if (result.data && result.data.items.length > 0) {
        const applicant = result.data.items[0];
        queryClient.setQueryData(applicantsKeys.short(applicant.id).queryKey, applicant);
        return applicant;
      }

      return undefined;
    },
    staleTime: 60 * 1000,
    enabled: !!id,
  });
};

export const useApplicantDetails = (id: number | undefined) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: applicantsKeys.detail(id).queryKey,
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("No id");
      const result = await apiApplicants.getById(id);
      if (result.error) throw new ApiErrorException(result.error);

      const existingShortData = queryClient.getQueryData<ApiApplicantShort>(
        applicantsKeys.short(id).queryKey
      );
      const updatedShortData = {
        ...result.data,
        have_unread_candidates: result.data?.have_unread_candidates ?? false,
        have_unread_mentions:
          existingShortData?.have_unread_mentions ?? result.data?.have_unread_mentions,
        last_or_unread_comment_data:
          existingShortData?.last_or_unread_comment_data ??
          result.data?.last_or_unread_comment_data,
      };

      queryClient.setQueryData(applicantsKeys.short(id).queryKey, updatedShortData);

      queryClient.setQueriesData<InfiniteData<ApiPagedData<ApiApplicantShort>>>(
        { queryKey: applicantsKeys.searchInfinite._def, exact: false },
        (data) => {
          if (!data) return data;

          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              items: page.items.map((applicant) =>
                applicant.id === id
                  ? {
                      ...applicant,
                      have_unread_candidates: false,
                    }
                  : applicant
              ),
            })),
          };
        }
      );

      return result.data;
    },
  });
};

export interface ApplicantSetPhotoParams {
  fileUUID: string;
  id: number;
}

export const useApplicantSetPhoto = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, fileUUID }: ApplicantSetPhotoParams) => {
      const result = await apiApplicants.setPhoto(id, fileUUID);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (applicant, variables) => {
      queryClient.setQueryData(applicantsKeys.detail(variables.id).queryKey, (oldData: unknown) => {
        if (oldData) return { ...oldData, photo_url: applicant.url };
      });
      queryClient.setQueryData(applicantsKeys.short(variables.id).queryKey, (oldData: unknown) => {
        if (oldData) return { ...oldData, photo_url: applicant.url };
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useApplicantDeletePhoto = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiApplicants.deletePhoto(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.setQueryData(applicantsKeys.detail(variables).queryKey, (oldData: unknown) => {
        if (oldData) return { ...oldData, photo_url: null };
      });
      queryClient.setQueryData(applicantsKeys.short(variables).queryKey, (oldData: unknown) => {
        if (oldData) return { ...oldData, photo_url: null };
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useApplicantDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiApplicants.delete(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.searchInfinite._def,
      });
      queryClient.invalidateQueries({
        queryKey: jobsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: jobsKeys.details._def,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export interface ApplicantsMergeParams {
  applicantId: number;
  similarApplicantId: number;
  resolveType: string;
}

export const useApplicantsMerge = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ applicantId, similarApplicantId, resolveType }: ApplicantsMergeParams) => {
      const result = await apiApplicants.merge(applicantId, similarApplicantId, resolveType);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.searchInfinite._def,
      });
      queryClient.invalidateQueries({
        queryKey: jobsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: jobsKeys.details._def,
      });
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.detail(variables.applicantId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: applicantActionsKeys.list(variables.applicantId).queryKey,
      });

      queryClient.invalidateQueries({
        queryKey: candidatesKeys.listByApplicantId(variables.applicantId).queryKey,
      });

      const candidates = queryClient.getQueryData<ApiCandidate[]>(
        candidatesKeys.listByApplicantId(variables.applicantId).queryKey
      );

      if (candidates) {
        candidates.forEach((candidate) => {
          queryClient.invalidateQueries({
            queryKey: candidateActionsKeys.list(candidate.id).queryKey,
          });
        });
      }
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useApplicantsUpdateLabels = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (payload: { applicantId: number; labelIds: number[] }) => {
      const result = await apiApplicants.updateLabels(payload.applicantId, payload.labelIds);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        applicantsKeys.detail(variables.applicantId).queryKey,
        (oldData: unknown) => {
          if (oldData && data) return { ...oldData, labels: data.labels };
        }
      );
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useApplicantUpdate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined,
  fieldMap: Record<string, string> = {}
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id, payload }: { id: number; payload: ApiApplicantSavePayload }) {
      const result = await apiApplicants.update(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.searchInfinite._def,
      });
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.detail(variables.id).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control, fieldMap);
    },
  });
};

export const useApplicantCreate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined,
  fieldMap: Record<string, string> = {}
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function (payload: ApiApplicantCreatePayload) {
      const result = await apiApplicants.create(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.searchInfinite._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control, fieldMap);
    },
  });
};

export const useApplicantCVUpdate = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: { cv_text: string; cv_id: number };
    }) => {
      const result = await apiApplicants.updateCV(id, payload.cv_text, payload.cv_id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.detail(variables.id).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useApplicantsMentions = () => {
  return useQuery({
    ...applicantsKeys.mentions(),
  });
};

export const useApplicantsAIRecommendationsSearchInfinite = (
  filter: ApiApplicantRecommendationFilter
) => {
  const queryClient = useQueryClient();

  return useInfiniteQuery({
    queryKey: applicantsKeys.aiRecommendations(filter).queryKey,
    queryFn: async ({ pageParam = 1, signal }) => {
      const result = await apiApplicants.recommendations(
        { ...filter, page_number: pageParam },
        signal
      );
      if (result.error) throw new ApiErrorException(result.error);
      result.data?.items.forEach((item) => {
        queryClient.setQueryData(applicantsKeys.short(item.id).queryKey, item);
        queryClient.setQueryData(
          candidatesKeys.listByApplicantId(item.id).queryKey,
          item.candidates
        );
      });
      return result.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPageParam) =>
      lastPageParam && lastPageParam.metadata.page_number < lastPageParam.metadata.total_pages
        ? lastPageParam.metadata.page_number + 1
        : undefined,
  });
};

export const useApplicantCVDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ applicantId, cvId }: { applicantId: number; cvId: number }) => {
      const result = await apiApplicants.deleteCV(applicantId, cvId);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.detail(variables.applicantId).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

// Fetching through the detail key also updates profiles already observing this applicant.
export const useApplicantCVPreviewRefetcher = (applicantId: number, cvId: number) => {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    const applicant = await queryClient.fetchQuery({
      queryKey: applicantsKeys.detail(applicantId).queryKey,
      queryFn: async () => {
        const result = await apiApplicants.getById(applicantId);
        if (result.error) throw new ApiErrorException(result.error);
        return result.data;
      },
      staleTime: 0,
    });
    return applicant?.cvs.find((cv) => cv.id === cvId)?.preview;
  }, [applicantId, cvId, queryClient]);
};
